// apps/lk/src/components/DiscMatrix.tsx
'use client';
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { DISC_TYPES, discCombination } from '@/lib/insights';

const COLORS: Record<string, string> = {
  D: '#dc2626',
  I: '#f59e0b',
  S: '#059669',
  C: '#2563eb',
};

const NAMES: Record<string, string> = {
  D: 'Доминирование',
  I: 'Влияние',
  S: 'Стабильность',
  C: 'Добросовестность',
};

type DiscData = {
  percent?: Record<string, number>;
  primaryType?: string;
  secondaryType?: string;
  dimensionScores?: Record<string, number>;
};

export function DiscMatrix({ disc }: { disc: DiscData | null | undefined }) {
  const [openKey, setOpenKey] = useState<string | null>(null);

  if (!disc?.percent && !disc?.dimensionScores) return null;

  const pct: Record<string, number> = disc.percent ||
    Object.fromEntries(
      Object.entries(disc.dimensionScores || {}).map(([k, v]) => [
        k.charAt(0).toUpperCase(),
        Number(v),
      ]),
    );

  const primary = disc.primaryType || Object.entries(pct).sort(([, a], [, b]) => b - a)[0]?.[0];
  const secondary = disc.secondaryType || Object.entries(pct).sort(([, a], [, b]) => b - a)[1]?.[0];

  // Позиция точки из процентов
  const task = (pct.D || 0) + (pct.C || 0);
  const people = (pct.I || 0) + (pct.S || 0);
  const fast = (pct.D || 0) + (pct.I || 0);
  const moderate = (pct.S || 0) + (pct.C || 0);
  const x = 50 + ((people - task) / Math.max(1, people + task)) * 50;
  const y = 50 + ((moderate - fast) / Math.max(1, moderate + fast)) * 50;

  const pMeta = primary ? DISC_TYPES[primary] : null;
  const combo = primary && secondary ? discCombination(primary, secondary) : '';

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-6 shadow-lg shadow-slate-200/50">
      {/* ══ Заголовок = тип ══ */}
      {pMeta && (
        <div className="mb-5 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl text-lg font-bold text-white" style={{ background: COLORS[primary!] }}>
            {primary}
          </span>
          <div>
            <p className="text-lg font-bold text-slate-900">{pMeta.title}</p>
            <p className="text-sm text-slate-400">{pMeta.subtitle}</p>
          </div>
        </div>
      )}

      {/* ══ Матрица (слева) + бары (справа) ══ */}
      <div className="grid gap-6 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* Матрица */}
        <div className="flex justify-center">
          <div className="relative" style={{ width: 220, height: 220 }}>
            {/* Верх / низ — горизонтальные подписи */}
            <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-full pb-1.5 whitespace-nowrap text-[9px] font-semibold uppercase tracking-widest text-slate-400">Быстрый</div>
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full pt-1.5 whitespace-nowrap text-[9px] font-semibold uppercase tracking-widest text-slate-400">Размеренный</div>
            {/* Лево / право — вертикальные подписи */}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full pr-2" style={{ writingMode: 'vertical-rl', transform: 'translateX(-100%) rotate(180deg)' }}>
              <span className="text-[9px] font-semibold uppercase tracking-widest text-slate-400">Задача</span>
            </div>
            <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full pl-2" style={{ writingMode: 'vertical-rl' }}>
              <span className="text-[9px] font-semibold uppercase tracking-widest text-slate-400">Люди</span>
            </div>

            {/* Сама матрица */}
            <div className="absolute inset-0 overflow-hidden border border-slate-200" style={{
              borderRadius: '48px',
              background: `
                radial-gradient(circle at 0% 0%, ${COLORS.D}cc 0%, transparent 55%),
                radial-gradient(circle at 100% 0%, ${COLORS.I}cc 0%, transparent 55%),
                radial-gradient(circle at 0% 100%, ${COLORS.C}cc 0%, transparent 55%),
                radial-gradient(circle at 100% 100%, ${COLORS.S}cc 0%, transparent 55%),
                #f8fafc
              `,
            }}>
              {/* Разделители */}
              <div className="absolute left-1/2 top-4 bottom-4 w-px bg-white/80" />
              <div className="absolute top-1/2 left-4 right-4 h-px bg-white/80" />

              {/* Буквы квадрантов */}
              {([
                { t: 'D', x: '20%', y: '20%' },
                { t: 'I', x: '80%', y: '20%' },
                { t: 'C', x: '20%', y: '80%' },
                { t: 'S', x: '80%', y: '80%' },
              ] as const).map(({ t, x: lx, y: ly }) => (
                <div key={t} className="absolute -translate-x-1/2 -translate-y-1/2 text-center" style={{ left: lx, top: ly }}>
                  <span className="block text-base font-black leading-none" style={{ color: COLORS[t], opacity: t === primary ? 1 : 0.35 }}>{t}</span>
                  <span className="block text-[7px] font-medium leading-tight text-slate-500">{DISC_TYPES[t].title}</span>
                </div>
              ))}

              {/* Точка */}
              <div className="absolute z-10 -translate-x-1/2 -translate-y-1/2" style={{ left: `${x}%`, top: `${y}%` }}>
                <div className="relative">
                  <div className="absolute -inset-2 rounded-full bg-slate-900/20 blur-sm" />
                  <div className="relative h-4 w-4 rounded-full border-2 border-white bg-slate-900 shadow-lg" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Горизонтальные бары D-I-S-C — кликабельные */}
        <div className="flex flex-col justify-center space-y-2">
          {(['D', 'I', 'S', 'C'] as const).map(t => {
            const v = pct[t] || 0;
            const isP = t === primary;
            const isOpen = openKey === t;
            const meta = DISC_TYPES[t];
            return (
              <div key={t} className={`overflow-hidden rounded-xl border border-l-2 border-slate-200 bg-white ${isP ? 'border-l-2' : ''}`} style={isP ? { borderLeftColor: COLORS[t] } : undefined}>
                <button
                  onClick={() => setOpenKey(isOpen ? null : t)}
                  className="w-full px-3 py-2.5 text-left transition hover:bg-slate-50"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold" style={{ color: COLORS[t] }}>{t}</span>
                      <span className="text-sm font-semibold text-slate-900">{NAMES[t]}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-500">{v}%</span>
                      <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} strokeWidth={2} />
                    </div>
                  </div>
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-200">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(4, v)}%`, background: COLORS[t], opacity: isP ? 1 : 0.5 }} />
                  </div>
                </button>
                {isOpen && meta && (
                  <div className="mx-3 mb-3 rounded-xl bg-slate-50 p-4">
                    <p className="mb-1 text-sm font-bold text-slate-900">{meta.title} — {meta.subtitle}</p>
                    <p className="text-sm leading-relaxed text-slate-600">{meta.description}</p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <div>
                        <p className="mb-1 text-xs font-semibold text-emerald-600">Сильные стороны</p>
                        <ul className="space-y-0.5">
                          {meta.strengths.map((s, i) => (
                            <li key={i} className="text-xs text-slate-600">{s}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="mb-1 text-xs font-semibold text-amber-600">Зоны роста</p>
                        <ul className="space-y-0.5">
                          {meta.growthAreas.map((s, i) => (
                            <li key={i} className="text-xs text-slate-600">{s}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="mt-3 rounded-lg bg-white p-2.5">
                      <p className="text-xs leading-relaxed text-slate-500">{meta.workStyle}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {/* Комбинация под барами */}
          {combo && (
            <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2">
              <p className="text-xs leading-relaxed text-slate-500">{combo}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
